import React from "react";
import CircularProgress from "@mui/material/CircularProgress";

export const EXT_COLORS = {
  pdf: "#E53935",
  doc: "#1565C0",
  docx: "#1565C0",
  xls: "#2E7D32",
  xlsx: "#2E7D32",
  csv: "#2E7D32",
  pptx: "#D84315",
  json: "#6A1B9A",
  txt: "#546E7A",
  md: "#546E7A",
  html: "#E65100",
  xml: "#E65100",
  yaml: "#795548",
  yml: "#795548",
  png: "#00838F",
  jpg: "#00838F",
  jpeg: "#00838F",
  gif: "#00838F",
  webp: "#00838F",
  svg: "#00838F",
};

export const TYPE_LABELS = {
  pdf: "PDF",
  doc: "Document",
  docx: "Document",
  xls: "Spreadsheet",
  xlsx: "Spreadsheet",
  csv: "Spreadsheet",
  pptx: "Presentation",
  json: "JSON",
  txt: "Text",
  md: "Markdown",
  html: "HTML",
  xml: "XML",
  yaml: "YAML",
  yml: "YAML",
  png: "Image",
  jpg: "Image",
  jpeg: "Image",
  gif: "Image",
  webp: "Image",
  svg: "Image",
};

export function getExt(fileName) {
  return (fileName || "").split(".").pop()?.toLowerCase() || "";
}

/**
 * Parse the [Attached files] block from a message string.
 * Returns { text, files[] } where text has the block stripped,
 * and files is an array of { fileName, meta, url }.
 */
export function parseAttachedFiles(content) {
  if (!content || typeof content !== "string") return { text: content, files: [] };
  const marker = "\n\n[Attached files]\n";
  const idx = content.indexOf(marker);
  if (idx === -1) return { text: content, files: [] };

  const text = content.slice(0, idx).trimEnd();
  const block = content.slice(idx + marker.length);
  const files = [];
  for (const line of block.split("\n")) {
    const trimmed = line.trim();
    if (!trimmed.startsWith("- ")) continue;
    // Format: "- filename.pdf (PDF, 1.2MB): https://..."
    const rest = trimmed.slice(2);
    const colonIdx = rest.lastIndexOf(": http");
    if (colonIdx === -1) continue;
    const beforeUrl = rest.slice(0, colonIdx);
    const url = rest.slice(colonIdx + 2);
    // Extract meta in parens
    const parenMatch = beforeUrl.match(/^(.+?)\s*\(([^)]+)\)$/);
    const fileName = parenMatch ? parenMatch[1].trim() : beforeUrl.trim();
    const meta = parenMatch ? parenMatch[2] : "";
    files.push({ fileName, meta, url });
  }
  return { text, files };
}

function FileTile({ attachment, onRemove }) {
  const ext = getExt(attachment.fileName);
  const color = EXT_COLORS[ext] || "#78909C";
  const typeLabel = TYPE_LABELS[ext] || ext.toUpperCase() || "File";
  const isUploading = attachment.uploading;
  const isError = Boolean(attachment.error);

  return (
    <div
      className={`chat-file-tile${isUploading ? " uploading" : ""}${isError ? " error" : ""}`}
    >
      <div className="chat-file-tile-icon" style={{ backgroundColor: color }}>
        {isUploading ? (
          <CircularProgress size={16} thickness={5} sx={{ color: "#fff" }} />
        ) : (
          ext.toUpperCase().slice(0, 4)
        )}
      </div>
      <div className="chat-file-tile-meta">
        <div className="chat-file-tile-name" title={attachment.fileName}>
          {attachment.fileName}
        </div>
        <div className="chat-file-tile-type">
          {isError ? "Upload failed" : typeLabel}
        </div>
      </div>
      <button
        className="chat-file-tile-remove"
        onClick={(e) => {
          e.stopPropagation();
          onRemove(attachment.id);
        }}
        aria-label="Remove file"
        type="button"
      >
        ×
      </button>
    </div>
  );
}

/**
 * Read-only file tile for rendered messages (no remove button).
 */
function MessageFileTile({ file }) {
  const ext = getExt(file.fileName);
  const color = EXT_COLORS[ext] || "#78909C";
  const typeLabel = TYPE_LABELS[ext] || ext.toUpperCase() || "File";

  return (
    <a
      href={file.url}
      target="_blank"
      rel="noopener noreferrer"
      className="chat-file-tile chat-file-tile-readonly"
      title={file.fileName}
      style={{ textDecoration: "none", color: "inherit" }}
    >
      <div className="chat-file-tile-icon" style={{ backgroundColor: color }}>
        {ext.toUpperCase().slice(0, 4)}
      </div>
      <div className="chat-file-tile-meta">
        <div className="chat-file-tile-name">{file.fileName}</div>
        <div className="chat-file-tile-type">
          {file.meta || typeLabel}
        </div>
      </div>
    </a>
  );
}

/**
 * Renders file attachment tiles for a rendered message.
 * Takes the parsed files array from parseAttachedFiles().
 */
export function MessageAttachmentTiles({ files }) {
  if (!files || files.length === 0) return null;
  return (
    <div className="chat-message-attachment-tiles">
      {files.map((f, i) => (
        <MessageFileTile key={i} file={f} />
      ))}
    </div>
  );
}

export default function AttachmentStrip({ attachments, onRemove }) {
  if (!attachments || attachments.length === 0) return null;
  return (
    <div className="chat-attachment-strip">
      {attachments.map((a) => (
        <FileTile key={a.id} attachment={a} onRemove={onRemove} />
      ))}
    </div>
  );
}
